import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import {
  ArrowRight,
  Ban,
  Check,
  Focus,
  HeartCrack,
  Network,
  ShieldAlert,
  Smile,
  Sparkles,
  Trophy,
  X,
} from 'lucide-react';
import { Container, Glow, Section, SectionHeader } from './ui';
import { Reveal, Stagger, fadeUp } from './motion';

const WORRIES = [
  { icon: ShieldAlert, title: 'Fear of Losing Money', text: 'No more wondering where your cash went with complete transparency and tracking.' },
  { icon: Ban, title: 'Lack of Trust', text: 'Transparency builds confidence among group members with visible activity for all.' },
  { icon: HeartCrack, title: 'Strained Relationships', text: 'Avoid awkward money fights with clear rules, records, and shared responsibility.' },
  { icon: Network, title: 'Complicated Processes', text: 'Skip the hassle of traditional banking—everyone doesn’t need a bank account to join.' },
];

const WINS = [
  { icon: Trophy, title: 'Goals Achieved', text: 'Actually get things done as a group and enjoy the rewards of your collective effort.' },
  { icon: Smile, title: 'A Fun Process', text: 'Working together becomes enjoyable, not stressful, with clear communication.' },
  { icon: Sparkles, title: 'Simplicity', text: 'Everything’s straightforward and user-friendly, designed with your needs in mind.' },
  { icon: Focus, title: 'Focus', text: 'Keep your eyes on the prize, not the problems, with streamlined group finance management.' },
];

const Column = ({ title, items, tone }) => {
  const isWin = tone === 'win';
  const Mark = isWin ? Check : X;
  return (
    <div className={clsx('surface p-6 sm:p-10', isWin && '!border-emerald-400/20')}>
      <Glow className={clsx('-top-24 h-64 w-64', isWin ? '-right-16 bg-emerald-500/15' : '-left-16 bg-rose-500/10')} />
      <div className="relative">
        <div className="flex items-center gap-3">
          <span
            className={clsx(
              'flex h-9 w-9 items-center justify-center rounded-full',
              isWin ? 'bg-emerald-400/15 text-emerald-300' : 'bg-rose-400/10 text-rose-300'
            )}
          >
            <Mark className="h-4 w-4" strokeWidth={3} />
          </span>
          <h3 className="text-2xl font-bold tracking-tight text-white">{title}</h3>
        </div>
        <Stagger as="ul" className="mt-8 space-y-3" stagger={0.08}>
          {items.map(({ icon: Icon, title: itemTitle, text }) => (
            <motion.li
              key={itemTitle}
              variants={fadeUp}
              className={clsx(
                'flex gap-4 rounded-2xl border p-4 transition-colors duration-300',
                isWin
                  ? 'border-emerald-400/10 bg-emerald-400/[0.03] hover:border-emerald-400/25'
                  : 'border-white/[0.05] bg-white/[0.02] hover:border-rose-400/20'
              )}
            >
              <Icon className={clsx('mt-0.5 h-5 w-5 shrink-0', isWin ? 'text-emerald-300' : 'text-rose-300/80')} />
              <div>
                <p className={clsx('font-semibold', isWin ? 'text-white' : 'text-slate-300')}>{itemTitle}</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-400">{text}</p>
              </div>
            </motion.li>
          ))}
        </Stagger>
      </div>
    </div>
  );
};

const Benefits = () => (
  <Section id="about" className="overflow-hidden">
    <Container className="relative">
      <SectionHeader
        eyebrow="The Difference"
        title="The Groupmoney Experience"
        description="How we transform your group finance experience from stress to success."
      />

      <div className="mt-16 grid gap-5 sm:mt-20 lg:grid-cols-2">
        <Reveal>
          <Column title="Say Goodbye to These Worries" items={WORRIES} tone="worry" />
        </Reveal>
        <Reveal delay={0.12}>
          <Column title="Say Hello to These Wins" items={WINS} tone="win" />
        </Reveal>
      </div>

      <Reveal className="mt-20 text-center">
        <a href="#get-started" className="btn-secondary group">
          Get Started and Experience the Difference
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
        </a>
        <p className="mx-auto mt-4 max-w-xl text-sm text-slate-500">
          Join thousands of groups who have already transformed their collaborative financial experience with Groupmoney.
        </p>
      </Reveal>
    </Container>
  </Section>
);

export default Benefits;
